/**
 * PortfolioLightbox — unified slideshow that handles both images and videos.
 * Used for the customer-facing pro portfolio strip on Instant Pricing.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

const PortfolioLightbox = ({ open, items = [], startIdx = 0, onClose, title }) => {
  const [idx, setIdx] = useState(startIdx);
  const videoRef = useRef(null);

  useEffect(() => { if (open) setIdx(startIdx); }, [open, startIdx]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + items.length) % items.length);
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, items.length]);

  // Pause + reset videos when switching slides.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [idx]);

  if (!open || items.length === 0) return null;
  const item = items[idx] || items[0];

  return (
    <div className="fixed inset-0 bg-black/85 z-[200] flex flex-col items-center justify-center p-4"
      data-testid="portfolio-lightbox" onClick={onClose}>
      <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          className="absolute -top-3 -right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg z-10"
          aria-label="Close" data-testid="portfolio-lightbox-close">
          <X size={18} weight="bold" />
        </button>

        <div className="bg-white rounded-md overflow-hidden">
          <div className="relative bg-black aspect-video flex items-center justify-center">
            {item.kind === 'video' ? (
              <video ref={videoRef} src={fullUrl(item.url)}
                poster={item.poster ? fullUrl(item.poster) : undefined}
                className="max-h-full max-w-full" controls autoPlay
                data-testid="portfolio-lightbox-video" />
            ) : (
              <img src={fullUrl(item.url)} alt={title || ''}
                className="max-h-full max-w-full object-contain"
                data-testid="portfolio-lightbox-image" />
            )}
            {items.length > 1 && (
              <>
                <button onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow"
                  aria-label="Previous" data-testid="portfolio-lightbox-prev">
                  <CaretLeft size={18} weight="bold" />
                </button>
                <button onClick={() => setIdx((i) => (i + 1) % items.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow"
                  aria-label="Next" data-testid="portfolio-lightbox-next">
                  <CaretRight size={18} weight="bold" />
                </button>
              </>
            )}
          </div>

          {title && (
            <div className="p-3 border-t border-[#E8E1D5] text-sm font-medium">
              {title} <span className="text-text-secondary font-normal">· {idx + 1} of {items.length}</span>
            </div>
          )}

          {items.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto p-2 border-t border-[#E8E1D5] bg-sand-50">
              {items.map((tp, ti) => (
                <button key={tp.id || ti} type="button" onClick={() => setIdx(ti)}
                  className={`flex-shrink-0 w-14 h-14 bg-black border-2 overflow-hidden relative ${
                    ti === idx ? 'border-brand-primary' : 'border-transparent'
                  }`} data-testid={`portfolio-lightbox-thumb-${ti}`}>
                  {tp.kind === 'video' ? (
                    <>
                      {tp.poster ? (
                        <img src={fullUrl(tp.poster)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <video src={fullUrl(tp.url)} className="w-full h-full object-cover" preload="metadata" muted />
                      )}
                      <span className="absolute bottom-0 right-0 text-[8px] uppercase tracking-wider font-bold bg-black/70 text-sand-50 px-1">▶</span>
                    </>
                  ) : (
                    <img src={fullUrl(tp.url)} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioLightbox;
